// One RTCPeerConnection per active live-view stream. The dashboard is the
// answerer; the Electron host creates the offer and the data channel.
//
// Wire pattern (matches streaming-host.html on the Electron side):
//   dashboard -> electron : { type: "start_stream", payload: { streamId, platform } }
//   electron  -> dashboard: { type: "webrtc_offer",        payload: { streamId, sdp, sdpType } }
//   dashboard -> electron : { type: "webrtc_answer",       payload: { streamId, sdp, sdpType } }
//   both      <-> ICE     : { type: "webrtc_ice_candidate", payload: { streamId, candidate } }
//   dashboard -> electron : { type: "stop_stream", payload: { streamId } }

import type { ControlClient, ControlMessage } from "./control";

export interface PeerOptions {
  control: ControlClient;
  iceServers: RTCIceServer[];
  onTrack: (stream: MediaStream) => void;
  onState: (state: RTCPeerConnectionState) => void;
}

export class StreamPeer {
  readonly streamId: string;
  readonly platform: string;
  private pc: RTCPeerConnection;
  private dc: RTCDataChannel | null = null;
  private control: ControlClient;
  private unsubs: Array<() => void> = [];
  private closed = false;

  constructor(platform: string, opts: PeerOptions) {
    this.streamId = `${platform}-${Math.random().toString(36).slice(2, 10)}`;
    this.platform = platform;
    this.control = opts.control;
    this.pc = new RTCPeerConnection({ iceServers: opts.iceServers });

    this.pc.ontrack = (ev) => {
      const stream = ev.streams[0] ?? new MediaStream([ev.track]);
      // Cut the receiver's jitter buffer down so user input feels responsive.
      // Default is browser-determined (~100ms). 50ms trades a tiny bit of
      // smoothness on packet loss for noticeably less perceived latency.
      try {
        const r = ev.receiver as RTCRtpReceiver & {
          jitterBufferTarget?: number;
          playoutDelayHint?: number;
        };
        if ("jitterBufferTarget" in r) r.jitterBufferTarget = 50;     // ms (W3C spec)
        if ("playoutDelayHint" in r) r.playoutDelayHint = 0.05;        // s (older Chromium)
      } catch {
        // not all browsers expose these — best effort
      }
      opts.onTrack(stream);
    };

    this.pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this.send("webrtc_ice_candidate", { candidate: ev.candidate.toJSON() });
      }
    };

    this.pc.onconnectionstatechange = () => {
      opts.onState(this.pc.connectionState);
      if (this.pc.connectionState === "failed" || this.pc.connectionState === "closed") {
        this.dispose();
      }
    };

    this.pc.ondatachannel = (ev) => {
      if (ev.channel.label === "input") {
        this.dc = ev.channel;
      }
    };

    // Subscribe to signaling for this stream id only.
    this.unsubs.push(opts.control.on("webrtc_offer", (msg) => this.handleOffer(msg)));
    this.unsubs.push(opts.control.on("webrtc_ice_candidate", (msg) => this.handleRemoteIce(msg)));
    this.unsubs.push(opts.control.on("stream_error", (msg) => {
      const p = (msg.payload || {}) as { streamId?: string; error?: string };
      if (p.streamId === this.streamId) {
        console.warn(`[peer] stream_error: ${p.error}`);
        this.dispose();
      }
    }));
  }

  start(): void {
    this.send("start_stream", {});
  }

  sendInput(event: Record<string, unknown>): void {
    if (!this.dc || this.dc.readyState !== "open") return;
    try {
      this.dc.send(JSON.stringify(event));
    } catch (err) {
      console.warn("[peer] dc send failed", err);
    }
  }

  dispose(): void {
    if (this.closed) return;
    this.closed = true;
    for (const u of this.unsubs) u();
    this.unsubs = [];
    if (this.dc && this.dc.readyState === "open") {
      try { this.dc.close(); } catch { /* */ }
    }
    if (this.pc.signalingState !== "closed") {
      try { this.pc.close(); } catch { /* */ }
    }
    this.send("stop_stream", {});
  }

  private async handleOffer(msg: ControlMessage): Promise<void> {
    const p = (msg.payload || {}) as { streamId?: string; sdp?: string; sdpType?: RTCSdpType };
    if (p.streamId !== this.streamId || !p.sdp) return;
    try {
      await this.pc.setRemoteDescription({ type: p.sdpType ?? "offer", sdp: p.sdp });
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      this.send("webrtc_answer", { sdp: answer.sdp, sdpType: answer.type });
    } catch (err) {
      console.error("[peer] failed to handle offer", err);
    }
  }

  private async handleRemoteIce(msg: ControlMessage): Promise<void> {
    const p = (msg.payload || {}) as { streamId?: string; candidate?: RTCIceCandidateInit };
    if (p.streamId !== this.streamId || !p.candidate) return;
    try {
      await this.pc.addIceCandidate(p.candidate);
    } catch (err) {
      console.warn("[peer] addIceCandidate failed", err);
    }
  }

  private send(type: string, extra: Record<string, unknown>): void {
    this.control.send({
      type,
      payload: { streamId: this.streamId, platform: this.platform, ...extra },
    });
  }
}
